import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Mail, Lock, Eye, EyeOff, LogIn, Loader,
  BarChart2, Package, Store, ShieldCheck
} from 'lucide-react';
import './Login.css';

const FEATURES = [
  { icon: BarChart2, text: 'Tableau de bord analytique en temps réel' },
  { icon: Package,   text: 'Gestion des stocks multi-boutiques' },
  { icon: Store,     text: 'Suivi des ventes et facturation' },
  { icon: ShieldCheck, text: 'Sécurité et accès basés sur les rôles' },
];

export default function Login() {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      navigate(result.user?.role === 'super-admin' ? '/super-admin' : '/dashboard');
    } else {
      setError(result.message || 'Email ou mot de passe incorrect');
    }
    setLoading(false);
  };

  return (
    <div className="lg-wrapper">

      {/* ── Left: form panel ── */}
      <div className="lg-left">
        <div className="lg-left-inner">

          {/* Logo */}
          <div className="lg-brand">
            <div className="lg-brand-icon">
              <Package size={22}/>
            </div>
            <span className="lg-brand-name">GestStock</span>
          </div>

          {/* Heading */}
          <div className="lg-heading">
            <h1 className="lg-title">Bon retour 👋</h1>
            <p className="lg-subtitle">
              Connectez-vous à votre espace de gestion pour accéder à votre tableau de bord.
            </p>
          </div>

          {/* Form */}
          <form className="lg-form" onSubmit={handleSubmit} noValidate>

            {error && (
              <div className="lg-error">
                <ShieldCheck size={14}/> {error}
              </div>
            )}

            <div className="lg-field">
              <label htmlFor="lg-email">Adresse email</label>
              <div className="lg-input-wrap">
                <Mail size={15} className="lg-input-icon"/>
                <input
                  id="lg-email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="lg-field">
              <label htmlFor="lg-password">Mot de passe</label>
              <div className="lg-input-wrap">
                <Lock size={15} className="lg-input-icon"/>
                <input
                  id="lg-password"
                  type={showPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lg-pwd-toggle"
                  onClick={() => setShowPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showPwd ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </div>

            <button className="lg-submit" type="submit" disabled={loading}>
              {loading
                ? <><Loader size={15} className="lg-spin"/> Connexion…</>
                : <><LogIn size={15}/> Se connecter</>}
            </button>
          </form>

          {/* Footer */}
          <p className="lg-footer-text">
            GestStock © {new Date().getFullYear()} — Système de gestion de stock
          </p>
        </div>
      </div>

      {/* ── Right: brand panel ── */}
      <div className="lg-right">
        <div className="lg-right-inner">
          <div className="lg-right-content">
            <div className="lg-right-badge">Plateforme SaaS</div>
            <h2 className="lg-right-title">
              Gérez votre stock<br/>avec efficacité
            </h2>
            <p className="lg-right-sub">
              Une solution complète pour piloter vos boutiques, vos stocks et vos ventes en un seul endroit.
            </p>

            <ul className="lg-features">
              {FEATURES.map(({ icon: Icon, text }, i) => (
                <li key={i} className="lg-feature">
                  <div className="lg-feature-icon"><Icon size={16}/></div>
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Decorative shapes */}
          <div className="lg-deco lg-deco--1"/>
          <div className="lg-deco lg-deco--2"/>
          <div className="lg-deco lg-deco--3"/>
        </div>

        <p className="lg-credit">Propulsé par GestStock SaaS</p>
      </div>

    </div>
  );
}
